/**
 * Local safety net for edits that failed to save.
 *
 * When a save fails (e.g. the session expired), the unsaved content is copied
 * to localStorage so it survives a page reload / re-login. On the next fresh
 * load of that file the app offers to restore it. A successful save clears
 * the backup.
 */

const PREFIX = 'leedoc_unsaved:';

export interface UnsavedBackup {
  content: string;
  /** Epoch ms when the backup was written */
  savedAt: number;
}

/** Scope backups per storage backend + project so paths never collide */
export function unsavedBackupKey(backend: string, projectId: string | null, path: string): string {
  return `${PREFIX}${backend}:${projectId ?? ''}:${path}`;
}

export function writeUnsavedBackup(key: string, content: string): boolean {
  try {
    localStorage.setItem(key, JSON.stringify({ content, savedAt: Date.now() } satisfies UnsavedBackup));
    return true;
  } catch {
    // Quota exceeded or storage unavailable
    return false;
  }
}

export function readUnsavedBackup(key: string): UnsavedBackup | null {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as UnsavedBackup;
    return typeof parsed?.content === 'string' ? parsed : null;
  } catch {
    return null;
  }
}

export function clearUnsavedBackup(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch {
    // ignore
  }
}
